import React, { useEffect, useState } from "react";
import posthog from "posthog-js";

import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";

import Button from "components/Button";
import InfoBox from "components/InfoBox";
import TabPanel from "components/TabPanel";
import PromoCodeModal from "./PromoCodeModal";
import SubscriptionInfoBox from "./SubscriptionInfoBox";
import WorkInProgressModal from "components/WorkInProgressModal";

import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  initSubscriptionPayment,
  SubscriptionKeys,
} from "store/features/subscriptionSlice";

import CancelSubscriptionBlock from "./CancelSubscriptionBlock";

import { subscriptionTabs } from "pages/Subscriptions/Subscriptions/variables";
import SubscriptionItem from "pages/Subscriptions/Subscriptions/SubscriptionItem";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";
import { featureFlag, testingVariants } from "shared/constants/posthog";
import { useSubscriptionStatusHandler } from "./useSubscriptionStatusHandler";

const FREE_SUBSCRIPTION_PLAN_ID = 1;

const SubscriptionMain: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentSubs = useAppSelector(
    (state) => state.subscription.subscription
  );

  const { isTrial, isCancelled, isExpired } = currentSubs || {};

  const canRedeemPromoCode =
    currentSubs?.paymentPlan.paymentPlanId === FREE_SUBSCRIPTION_PLAN_ID;

  const [activePromocode, setActivePromocode] = useState<string | null>(null);
  const [visibleTab, setVisibleTab] = useState<string>(subscriptionTabs[0].id);
  const [isCustomSubsModalOpen, setIsCustomSubsModalOpen] = useState(false);
  const [isPromoCodeModalOpen, setIsPromoCodeModalOpen] = useState(false);

  const showFreeSubscription = (() => {
    const variant = posthog.getFeatureFlag(
      featureFlag.free_subscription_for_trial
    );
    return variant !== testingVariants.TEST;
  })();

  const isSubsActive = !isCancelled && !isExpired;

  const isFreePlan =
    currentSubs && currentSubs?.paymentPlan.paymentPlanId === 1 && !isTrial;

  useSubscriptionStatusHandler(setVisibleTab);

  useEffect(() => {
    const promocode = localStorage.getItem(SubscriptionKeys.PROMOCODE);
    const timestamp = localStorage.getItem(
      SubscriptionKeys.PROMOCODE_TIMESTAMP
    );

    if (canRedeemPromoCode && promocode && timestamp) {
      const timeDiff = Date.now() - parseInt(timestamp, 10);
      const oneDay = 24 * 60 * 60 * 1000;

      if (timeDiff > oneDay) {
        localStorage.removeItem(SubscriptionKeys.PROMOCODE);
        localStorage.removeItem(SubscriptionKeys.PROMOCODE_TIMESTAMP);
        setActivePromocode(null);
      } else {
        setActivePromocode(promocode);
      }
    } else {
      setActivePromocode(null);
    }
  }, [canRedeemPromoCode, isPromoCodeModalOpen]);

  const openCustomSubsModal = () => {
    setIsCustomSubsModalOpen(true);
  };

  const closeCustomSubsModal = () => {
    setIsCustomSubsModalOpen(false);
  };

  const openPromoCodeModal = () => {
    analytics().sendConversionEvent(
      AnalyticsEvents.subscription_use_promocode_btn,
      null,
      true
    );
    setIsPromoCodeModalOpen(true);
  };

  const closePromoCodeModal = () => {
    setIsPromoCodeModalOpen(false);
  };

  const onChooseSubscription = async (
    paymentPlanId?: number,
    modifierId?: string
  ) => {
    if (modifierId === "custom") {
      analytics().sendEvent(AnalyticsEvents.iban_subscription_premium_custom);
      openCustomSubsModal();
      return;
    }

    analytics().sendEvent(
      `${AnalyticsEvents.subscription_choose_plan_}${paymentPlanId}`
    );

    activePromocode &&
      analytics().sendConversionEvent(
        AnalyticsEvents.subscription_choose_with_promocode,
        null,
        true
      );

    if (paymentPlanId) {
      const { url } = await dispatch(
        initSubscriptionPayment({
          paymentPlanId,
          promocode: activePromocode || "",
        })
      ).unwrap();

      location.assign(url);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setVisibleTab(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        width="100%"
        sx={{ mb: { xs: 2, sm: 3 } }}
      >
        <Box sx={{ mb: { xs: 2, sm: 0 } }}>
          <Typography sx={{ mb: 0.5 }} variant="h3">
            Тарифи
          </Typography>
          <Typography variant="body1" color="grey700">
            Оберіть свій тарифний план
          </Typography>
        </Box>
        {canRedeemPromoCode && (
          <Button
            variant="contained"
            color="secondary"
            onClick={openPromoCodeModal}
            withBorder
            disabled={!!activePromocode}
          >
            {activePromocode ? "Промокод активовано" : "Використати промокод"}
          </Button>
        )}
      </Box>
      <Box sx={{ mb: 5 }}></Box>

      {canRedeemPromoCode && activePromocode ? (
        <Box sx={{ mb: 3, width: "100%" }}>
          <InfoBox
            dataTestId="promocode-info-box"
            title={`Успіх! Промокод ${activePromocode} активовано`}
            content={
              "Оформіть будь-яку підписку та отримайте +1 місяць безкоштовно."
            }
          />
        </Box>
      ) : (
        currentSubs && <SubscriptionInfoBox currentSubs={currentSubs} />
      )}

      <Box sx={styles.tabWrapper}>
        <Tabs value={visibleTab} onChange={handleTabChange} variant="fullWidth">
          {subscriptionTabs.map((tab) => (
            <Tab
              sx={{ "&.Mui-selected": { color: "grey900" } }}
              key={tab.id}
              value={tab.id}
              label={tab.label}
              data-testid={`subs-tab-${tab.id}`}
            />
          ))}
        </Tabs>
        <Chip label="-16%" sx={styles.tabChip} />
      </Box>
      {subscriptionTabs.map((tab) => (
        <TabPanel key={`panel-${tab.id}`} value={visibleTab} index={tab.id}>
          <Box sx={styles.tabPanelWrapper}>
            {tab.subscriptions.map((card) => {
              if (card.id.startsWith("free") && !showFreeSubscription) {
                return null;
              }

              return (
                <SubscriptionItem
                  key={card.id}
                  card={card}
                  onChooseSubscription={onChooseSubscription}
                  period={tab.period}
                  currentSubscription={currentSubs}
                  customBtnText="Отримати пропозицію"
                />
              );
            })}
          </Box>
        </TabPanel>
      ))}

      <WorkInProgressModal
        source="Індивідуальний тариф"
        title="Індивідуальний тариф"
        description="Залиште свій номер, і ми зв’яжемося з вами для обговорення деталей."
        isOpen={isCustomSubsModalOpen}
        closeModal={closeCustomSubsModal}
      />

      <PromoCodeModal
        isOpen={isPromoCodeModalOpen}
        onClose={closePromoCodeModal}
      />

      {currentSubs && isSubsActive && !isFreePlan && (
        <CancelSubscriptionBlock subscription={currentSubs} />
      )}
    </Box>
  );
};

const styles = {
  tabWrapper: {
    ml: "auto",
    mr: "auto",
    mb: 3,
    maxWidth: "248px",
    position: "relative",
  },
  tabChip: {
    position: "absolute",
    top: -13,
    right: -25,
    borderRadius: 2,
    height: 26,
    "& .MuiChip-label": {
      padding: "2px 8px",
    },
    backgroundColor: "#3FA93A",
    color: "#FFFFFF",
  },
  tabPanelWrapper: {
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "column",
    alignItems: "center",
    "@media (min-width: 1100px)": {
      alignItems: "stretch",
      flexDirection: "row",
    },
    gap: 2,
    mb: 3,
  },
};

export default SubscriptionMain;
